/**
 * RndReport — "R&D / SR&ED Report" sub-page of the Coaching tab (/coach/rnd).
 *
 * Aggregates the R&D blocks the Day Audit tags across a claim period into a
 * CRA-T661-aligned report: per-project technological uncertainties, work
 * performed, advancement sought, conclusions, hours, plus a dated evidence
 * appendix. Built from data already captured by the Day Audit — no new
 * tracking burden.
 *
 * Flow:
 *   1. Pick a claim period (defaults to the current calendar year).
 *   2. A no-AI rollup (rnd-summary) shows total R&D hours, by project, by month.
 *   3. "Generate CRA report" calls Claude (rnd-report) → structured narrative.
 *   4. "Print / Save as PDF" opens a clean print layout via window.print().
 *
 * NOT tax advice — this is an evidence trail; eligibility + the T661 filing
 * should be confirmed with a SR&ED specialist / accountant.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../App";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/time-audit";
// Must match RND_CATEGORY in api/time-audit.js.
const RND_CATEGORY = "R&D / Experimental Development";

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtHrs(h) {
  const n = Number(h) || 0;
  return `${n.toLocaleString("en-CA", { maximumFractionDigits: 1 })} h`;
}
function fmtDateLong(yyyymmdd) {
  if (!yyyymmdd) return "—";
  const [y, m, d] = String(yyyymmdd).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}
function fmtMonth(ym) {
  const [y, m] = String(ym).split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-CA", { month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function fmtTimeShort(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
}
function fmtMinFromSec(sec) {
  const m = Math.round((Number(sec) || 0) / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

export default function RndReport() {
  const { session: authSession } = useSession();
  const navigate = useNavigate();
  const userEmail = (authSession?.user?.email || "").toLowerCase();

  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(todayLocal());

  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState(null);
  const [savedList, setSavedList] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [rescan, setRescan] = useState(null); // { total, done, current, found }
  const [blocks, setBlocks] = useState([]);
  const [showAllBlocks, setShowAllBlocks] = useState(false);
  const [categories, setCategories] = useState([]);

  // ── No-AI rollup for the chosen range ──────────────────────────────────────
  const loadSummary = useCallback(async () => {
    if (!userEmail || !from || !to) return;
    setBusy("summary"); setError("");
    try {
      const r = await apiFetch(`${API}?action=rnd-summary&user=${encodeURIComponent(userEmail)}&from=${from}&to=${to}`).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Failed to load summary");
      setSummary(r);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [userEmail, from, to]);

  const loadSavedList = useCallback(async () => {
    if (!userEmail) return;
    try {
      const r = await apiFetch(`${API}?action=list-rnd-reports&user=${encodeURIComponent(userEmail)}`).then(r => r.json());
      if (r.ok) setSavedList(r.reports || []);
    } catch { /* swallow */ }
  }, [userEmail]);

  // The activity ledger — what was worked on in the range (editable).
  const loadBlocks = useCallback(async () => {
    if (!userEmail || !from || !to) return;
    try {
      const r = await apiFetch(`${API}?action=rnd-blocks&user=${encodeURIComponent(userEmail)}&from=${from}&to=${to}${showAllBlocks ? "&all=1" : ""}`).then(r => r.json());
      if (r.ok) setBlocks(r.blocks || []);
    } catch { /* swallow */ }
  }, [userEmail, from, to, showAllBlocks]);

  const loadCategories = useCallback(async () => {
    if (!userEmail) return;
    try {
      const r = await apiFetch(`${API}?action=list-categories&user=${encodeURIComponent(userEmail)}`).then(r => r.json());
      if (r.ok) setCategories(r.categories || []);
    } catch { /* swallow */ }
  }, [userEmail]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadSavedList(); }, [loadSavedList]);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Persist an inline edit to a ledger block, then refresh ledger + rollup.
  async function saveBlock(patch) {
    setBusy("block"); setError("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "clarify-block", ...patch }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Could not save block");
      await Promise.all([loadBlocks(), loadSummary()]);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  // ── Generate the AI report for the range ───────────────────────────────────
  async function generate() {
    if (!userEmail) return;
    setBusy("report"); setError(""); setReport(null);
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "rnd-report", user_email: userEmail, from, to }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Report generation failed");
      setReport(r.report);
      await loadSavedList();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  async function openSaved(id) {
    setBusy("opening"); setError("");
    try {
      const r = await apiFetch(`${API}?action=get-rnd-report&id=${id}`).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Could not open report");
      setReport(r.report);
      if (r.meta) { setFrom(r.meta.period_start); setTo(r.meta.period_end); }
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  // Re-audit every already-audited day in the range so the new R&D detection
  // retroactively tags old experimental-development work into its category.
  // Client-driven + sequential (like the Day Audit backfill) so it survives the
  // serverless function time limit.
  async function rescanForRnd() {
    if (!userEmail) return;
    setError("");
    let sessions = [];
    try {
      const r = await apiFetch(`${API}?action=list-sessions-range&user=${encodeURIComponent(userEmail)}&from=${from}&to=${to}`).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Could not list sessions");
      sessions = (r.sessions || []).filter(s => s.audited);
    } catch (e) { setError(e.message); return; }
    if (!sessions.length) { setError("No audited days found in this range to re-scan."); return; }
    if (!window.confirm(`Re-scan ${sessions.length} audited day${sessions.length === 1 ? "" : "s"} for R&D? This re-runs the AI audit on each day (uses AI credits) and may take a while.`)) return;

    // Oldest first so progress reads naturally.
    sessions.sort((a, b) => a.day.localeCompare(b.day));
    setRescan({ total: sessions.length, done: 0, current: sessions[0].day, found: 0 });
    let found = 0;
    for (let i = 0; i < sessions.length; i++) {
      const s = sessions[i];
      setRescan({ total: sessions.length, done: i, current: s.day, found });
      try {
        const r = await apiFetch(API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "audit-day", session_id: s.id }),
        }).then(r => r.json());
        if (r.ok) found += 1;
      } catch { /* skip this day, keep going */ }
    }
    setRescan({ total: sessions.length, done: sessions.length, current: null, found });
    await Promise.all([loadSummary(), loadBlocks()]);
    setTimeout(() => setRescan(null), 6000);
  }

  function applyPreset(p) {
    const y = new Date().getFullYear();
    if (p === "this-year") { setFrom(`${y}-01-01`); setTo(todayLocal()); }
    else if (p === "last-year") { setFrom(`${y - 1}-01-01`); setTo(`${y - 1}-12-31`); }
  }

  const byProjectRows = useMemo(
    () => Object.entries(summary?.by_project || {}).sort((a, b) => b[1] - a[1]),
    [summary]
  );
  const byMonthRows = useMemo(
    () => Object.entries(summary?.by_month || {}).sort((a, b) => a[0].localeCompare(b[0])),
    [summary]
  );
  const maxMonth = useMemo(() => Math.max(1, ...byMonthRows.map(([, v]) => v)), [byMonthRows]);

  return (
    <div style={S.page} className="rnd-print">
      <style>{PRINT_CSS}</style>

      <div style={S.header} className="no-print">
        <div>
          <button onClick={() => navigate("/coach/day-audit")} style={S.backBtn}>‹ Back to Day Audit</button>
          <div style={S.title}>R&amp;D / SR&amp;ED Report</div>
          <div style={S.subtitle}>
            Turns the R&amp;D the Day Audit captures into a CRA-ready evidence trail. Pick a claim period and generate the write-up.
          </div>
        </div>
      </div>

      {error && <div style={S.errorBanner} className="no-print">{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}

      {/* Controls */}
      <div style={S.card} className="no-print">
        <div style={{ padding: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label style={S.controlLabel}>From</label>
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={S.dateInput} />
          <label style={S.controlLabel}>To</label>
          <input type="date" value={to} min={from} max={todayLocal()} onChange={e => setTo(e.target.value)} style={S.dateInput} />
          <button onClick={() => applyPreset("this-year")} style={S.btnSm}>This year</button>
          <button onClick={() => applyPreset("last-year")} style={S.btnSm}>Last year</button>
          <div style={{ flex: 1 }} />
          <button onClick={rescanForRnd} disabled={!!busy || !!rescan} style={S.btn} title="Re-run the AI audit on every audited day in this range so old R&D work gets tagged into the new category">
            {rescan ? "Re-scanning…" : "Re-scan past days for R&D"}
          </button>
          <button onClick={generate} disabled={!!busy || !!rescan} style={S.btnPrimary}>
            {busy === "report" ? "Generating… (≈30s)" : "Generate CRA report"}
          </button>
          {report && (
            <button onClick={() => window.print()} style={S.btn}>Print / Save as PDF</button>
          )}
        </div>
        {rescan && (
          <div style={{ padding: "0 16px 16px" }}>
            <div style={{ fontSize: 12, color: "#a5b4fc", marginBottom: 6 }}>
              {rescan.current
                ? `Re-auditing ${rescan.current}… (${rescan.done}/${rescan.total})`
                : `Done — re-audited ${rescan.done} day${rescan.done === 1 ? "" : "s"}. Refreshing rollup…`}
            </div>
            <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${(rescan.done / Math.max(rescan.total, 1)) * 100}%`, height: "100%", background: "#818cf8", transition: "width 0.3s" }} />
            </div>
          </div>
        )}
      </div>

      {/* Rollup (no AI) */}
      {summary && (
        <div style={S.card} className="no-print">
          <div style={S.cardHeader}>
            <div style={S.cardTitle}>R&amp;D in this period</div>
            <div style={S.cardSubtitle}>
              {fmtHrs(summary.total_hours)} · {summary.rnd_days} day{summary.rnd_days === 1 ? "" : "s"} · {summary.block_count} block{summary.block_count === 1 ? "" : "s"}
            </div>
          </div>
          <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            <div>
              <div style={S.miniHead}>By project</div>
              {byProjectRows.length === 0 && <div style={S.muted}>No R&amp;D tracked yet in this range.</div>}
              {byProjectRows.map(([proj, hrs]) => (
                <div key={proj} style={S.kvRow}>
                  <span style={{ flex: 1 }}>{proj}</span>
                  <span style={{ fontWeight: 700 }}>{fmtHrs(hrs)}</span>
                </div>
              ))}
            </div>
            <div>
              <div style={S.miniHead}>By month</div>
              {byMonthRows.length === 0 && <div style={S.muted}>—</div>}
              {byMonthRows.map(([ym, hrs]) => (
                <div key={ym} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 80, fontSize: 12, color: "#8892a4" }}>{fmtMonth(ym)}</span>
                  <div style={{ flex: 1, height: 10, background: "rgba(255,255,255,0.05)", borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ width: `${(hrs / maxMonth) * 100}%`, height: "100%", background: "#818cf8" }} />
                  </div>
                  <span style={{ width: 52, textAlign: "right", fontSize: 12, fontWeight: 600 }}>{fmtHrs(hrs)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Activity ledger — what was worked on (editable; the CRA report draws from this) */}
      <BlockLedger
        blocks={blocks}
        categories={categories}
        showAll={showAllBlocks}
        onToggleShowAll={() => setShowAllBlocks(v => !v)}
        onSave={saveBlock}
        busy={busy}
      />

      {busy === "report" && (
        <div style={{ ...S.card, padding: 20, textAlign: "center", color: "#8892a4" }} className="no-print">
          Clustering R&amp;D blocks into projects and writing T661-aligned narratives…
        </div>
      )}

      {/* The report itself (printable) */}
      {report && <ReportView report={report} userEmail={userEmail} />}

      {/* Saved reports */}
      {savedList.length > 0 && (
        <div style={S.card} className="no-print">
          <div style={S.cardHeader}><div style={S.cardTitle}>Saved reports</div></div>
          <div style={{ padding: 16 }}>
            {savedList.map(r => (
              <div key={r.id} style={S.savedRow}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: "#e8eaf0", fontWeight: 600 }}>
                    {fmtDateLong(r.period_start)} → {fmtDateLong(r.period_end)}
                  </div>
                  <div style={{ fontSize: 11, color: "#8892a4" }}>
                    {fmtHrs(r.total_hours)} · generated {fmtDateTime(r.generated_at)}
                  </div>
                </div>
                <button onClick={() => openSaved(r.id)} disabled={!!busy} style={S.btnSm}>Open</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── The printable report body ────────────────────────────────────────────────
function ReportView({ report, userEmail }) {
  const p = report || {};
  return (
    <div style={S.reportDoc}>
      <div style={S.reportHeader}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>SR&amp;ED R&amp;D Activity Report</div>
        <div style={{ fontSize: 13, color: "#8892a4", marginTop: 4 }}>
          {fmtDateLong(p.period?.start)} – {fmtDateLong(p.period?.end)} · {userEmail}
        </div>
        <div style={{ display: "flex", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
          <Stat label="Total R&D hours" value={fmtHrs(p.total_hours)} />
          <Stat label="Active R&D days" value={String(p.rnd_days ?? 0)} />
          <Stat label="Projects" value={String((p.projects || []).length)} />
        </div>
        {p.overview && <p style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6, marginTop: 16, marginBottom: 0 }}>{p.overview}</p>}
        {p.github && (
          <div style={{ fontSize: 11, color: p.github.included ? "#22c55e" : "#8892a4", marginTop: 10 }}>
            {p.github.included
              ? `GitHub evidence: ${p.github.count} commit${p.github.count === 1 ? "" : "s"} from ${p.github.repo} included.`
              : `GitHub commits not included${p.github.reason ? ` — ${p.github.reason}` : ""}.`}
          </div>
        )}
      </div>

      {(p.projects || []).map((proj, i) => (
        <div key={i} style={S.projectBlock}>
          <div style={S.projectTitle}>
            {i + 1}. {proj.name}
            <span style={{ float: "right", fontSize: 14, color: "#818cf8", fontWeight: 700 }}>{fmtHrs(proj.hours)}</span>
          </div>
          <Field label="Technological uncertainties" body={proj.technological_uncertainties} />
          <Field label="Work performed (systematic investigation)" body={proj.work_performed} />
          <Field label="Technological advancement sought" body={proj.advancement_sought} />
          <Field label="Conclusions" body={proj.conclusions} />

          {(proj.commits || []).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={S.fieldLabel}>Source-code commits ({proj.commits.length})</div>
              <div style={{ marginTop: 6 }}>
                {proj.commits.map((c, j) => (
                  <div key={j} style={{ display: "flex", gap: 10, padding: "4px 0", borderTop: j ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                    <span style={{ width: 92, flexShrink: 0, fontSize: 11, color: "#8892a4" }}>{fmtDateLong(c.date ? c.date.slice(0, 10) : null)}</span>
                    <code style={{ fontSize: 11, color: "#818cf8", flexShrink: 0 }}>{c.sha}</code>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "#cbd5e1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.message}>{c.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(proj.evidence || []).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={S.fieldLabel}>Evidence log ({proj.evidence.length} session{proj.evidence.length === 1 ? "" : "s"})</div>
              <div style={{ marginTop: 6 }}>
                {proj.evidence.map((ev, j) => (
                  <div key={j} style={S.evRow}>
                    <div style={{ width: 92, flexShrink: 0, fontSize: 11, color: "#8892a4" }}>{fmtDateLong(ev.day)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "#e8eaf0" }}>
                        {ev.activity || "R&D activity"}
                        {ev.minutes != null && <span style={{ color: "#8892a4" }}> · {ev.minutes} min</span>}
                      </div>
                      {(ev.items || []).length > 0 && (
                        <div style={{ fontSize: 11, color: "#8892a4", marginTop: 2, lineHeight: 1.5 }}>
                          {ev.items.slice(0, 8).map((it, k) => (
                            <div key={k} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.ref}>
                              • {it.app ? `${it.app}: ` : ""}{it.ref}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {(p.daily_notes || []).length > 0 && (
        <div style={S.projectBlock}>
          <div style={S.projectTitle}>Contemporaneous daily notes</div>
          {p.daily_notes.map((n, i) => (
            <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
              <div style={{ fontSize: 12, color: "#818cf8", fontWeight: 600 }}>{fmtDateLong(n.day)}{n.projects ? ` · ${n.projects}` : ""}</div>
              <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.5, marginTop: 2 }}>{n.note_text}</div>
            </div>
          ))}
        </div>
      )}

      {(p.unmatched_commits || []).length > 0 && (
        <div style={S.projectBlock}>
          <div style={S.projectTitle}>Other commits in period ({p.unmatched_commits.length})</div>
          <div style={{ fontSize: 11, color: "#8892a4", marginBottom: 6 }}>
            Dated commits not tied to a tracked R&amp;D block — included as supplementary evidence of work performed.
          </div>
          {p.unmatched_commits.map((c, j) => (
            <div key={j} style={{ display: "flex", gap: 10, padding: "4px 0", borderTop: j ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
              <span style={{ width: 92, flexShrink: 0, fontSize: 11, color: "#8892a4" }}>{fmtDateLong(c.date ? c.date.slice(0, 10) : null)}</span>
              <code style={{ fontSize: 11, color: "#818cf8", flexShrink: 0 }}>{c.sha}</code>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "#cbd5e1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.message}>{c.message}</span>
            </div>
          ))}
        </div>
      )}

      {p.disclaimer && (
        <div style={S.disclaimer}>{p.disclaimer}</div>
      )}
    </div>
  );
}

// ── Activity ledger — reviewable + editable list of blocks ───────────────────
function BlockLedger({ blocks, categories, showAll, onToggleShowAll, onSave, busy }) {
  const grouped = useMemo(() => {
    const m = new Map();
    for (const b of blocks) {
      const d = b.day || "—";
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(b);
    }
    return [...m.entries()].sort((a, c) => String(a[0]).localeCompare(String(c[0])));
  }, [blocks]);

  const rndCount = useMemo(() => blocks.filter(b => b.category === RND_CATEGORY).length, [blocks]);

  return (
    <div style={S.card} className="no-print">
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>
          R&amp;D activity log
          <span style={{ marginLeft: 8, fontSize: 12, color: "#8892a4", fontWeight: 400 }}>
            {rndCount} R&amp;D block{rndCount === 1 ? "" : "s"}{showAll ? ` · ${blocks.length} total shown` : ""}
          </span>
        </div>
        <label style={{ fontSize: 12, color: "#8892a4", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={showAll} onChange={onToggleShowAll} />
          Show all activity (to pull a mis-tagged block into R&amp;D)
        </label>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 12, lineHeight: 1.5 }}>
          This is what the audit captured for the selected period — the CRA report draws directly from these blocks. Click <strong style={{ color: "#cbd5e1" }}>Edit</strong> on any row to correct the category or wording before generating the report.
        </div>
        {blocks.length === 0 && (
          <div style={S.muted}>No {showAll ? "" : "R&D "}activity in this range yet. Run a re-scan, or widen the dates.</div>
        )}
        {grouped.map(([day, rows]) => (
          <div key={day} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#818cf8", margin: "8px 0 4px" }}>{fmtDateLong(day)}</div>
            {rows.map(b => (
              <BlockRow key={b.id} block={b} categories={categories} onSave={onSave} busy={busy} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockRow({ block, categories, onSave, busy }) {
  const [editing, setEditing] = useState(false);
  const [activity, setActivity] = useState(block.activity || "");
  const [category, setCategory] = useState(block.category || "");
  const rd = block.rd_json || {};
  const [project, setProject] = useState(rd.project || "");
  const [uncertainty, setUncertainty] = useState(rd.uncertainty || "");
  const [work, setWork] = useState(rd.work || "");
  const [advancement, setAdvancement] = useState(rd.advancement || "");

  const isRnd = block.category === RND_CATEGORY;
  const catNames = useMemo(() => {
    const names = categories.map(c => c.name);
    if (!names.includes(RND_CATEGORY)) names.unshift(RND_CATEGORY);
    return names;
  }, [categories]);

  function reset() {
    setActivity(block.activity || ""); setCategory(block.category || "");
    setProject(rd.project || ""); setUncertainty(rd.uncertainty || "");
    setWork(rd.work || ""); setAdvancement(rd.advancement || "");
    setEditing(false);
  }
  function save() {
    const willBeRnd = category === RND_CATEGORY;
    const patch = { id: block.id, activity: activity.trim(), category };
    patch.rd_json = willBeRnd
      ? { project: project.trim() || null, uncertainty: uncertainty.trim() || null, work: work.trim() || null, advancement: advancement.trim() || null, evidence: rd.evidence || [] }
      : null;
    onSave(patch);
    setEditing(false);
  }

  if (!editing) {
    return (
      <div style={S.ledgerRow}>
        <div style={{ width: 96, flexShrink: 0, fontSize: 11, color: "#8892a4" }}>
          {fmtTimeShort(block.started_at)}<br />{fmtMinFromSec(block.duration_sec)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "#e8eaf0" }}>
            {block.activity || "—"}
            {isRnd && <span style={S.rndPill}>R&amp;D</span>}
          </div>
          <div style={{ fontSize: 11, color: "#8892a4", marginTop: 2 }}>{block.category}</div>
          {isRnd && rd.uncertainty && <div style={S.rdLine}><b>Uncertainty:</b> {rd.uncertainty}</div>}
          {isRnd && rd.work && <div style={S.rdLine}><b>Work:</b> {rd.work}</div>}
          {isRnd && rd.advancement && <div style={S.rdLine}><b>Advancement:</b> {rd.advancement}</div>}
          {isRnd && rd.project && <div style={{ ...S.rdLine, color: "#a5b4fc" }}>Project: {rd.project}</div>}
        </div>
        <button onClick={() => setEditing(true)} disabled={!!busy} style={S.btnSm}>Edit</button>
      </div>
    );
  }

  return (
    <div style={{ ...S.ledgerRow, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#8892a4" }}>{fmtTimeShort(block.started_at)} · {fmtMinFromSec(block.duration_sec)}</span>
        <select value={category} onChange={e => setCategory(e.target.value)} style={S.select}>
          {catNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <input value={activity} onChange={e => setActivity(e.target.value)} placeholder="Activity" style={S.txtInput} />
      {category === RND_CATEGORY && (
        <>
          <input value={project} onChange={e => setProject(e.target.value)} placeholder="Project (e.g. AI Quoting Engine)" style={S.txtInput} />
          <textarea value={uncertainty} onChange={e => setUncertainty(e.target.value)} placeholder="Technological uncertainty — what couldn't be known with standard practice?" style={S.miniArea} />
          <textarea value={work} onChange={e => setWork(e.target.value)} placeholder="Work performed — the experimentation/iteration you did" style={S.miniArea} />
          <textarea value={advancement} onChange={e => setAdvancement(e.target.value)} placeholder="Advancement sought — the capability/knowledge you were after" style={S.miniArea} />
        </>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={reset} style={S.btnSm}>Cancel</button>
        <button onClick={save} disabled={!!busy} style={S.btnPrimary}>Save</button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#e8eaf0" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#8892a4" }}>{label}</div>
    </div>
  );
}
function Field({ label, body }) {
  if (!body) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={S.fieldLabel}>{label}</div>
      <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6, marginTop: 3, whiteSpace: "pre-wrap" }}>{body}</div>
    </div>
  );
}

// Print rules use !important so they override the dark inline styles when the
// user saves the report as a PDF. Everything tagged .no-print is hidden.
const PRINT_CSS = `
@media print {
  .no-print { display: none !important; }
  body { background: #ffffff !important; }
  .rnd-print, .rnd-print * {
    color: #111 !important;
    background: #ffffff !important;
    border-color: #ddd !important;
    box-shadow: none !important;
  }
  .rnd-print { padding: 0 !important; max-width: none !important; }
}
`;

const S = {
  page: { padding: "24px 28px 60px", color: "#e8eaf0", fontFamily: "'Roboto', sans-serif", maxWidth: 1100, margin: "0 auto" },
  header: { marginBottom: 18 },
  backBtn: { background: "none", border: "none", color: "#a5ce3a", fontSize: 14, cursor: "pointer", padding: 0, marginBottom: 6, fontFamily: "inherit" },
  title: { fontSize: 28, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.02em", lineHeight: 1.1 },
  subtitle: { fontSize: 13, color: "#8892a4", marginTop: 4, maxWidth: 620 },

  errorBanner: { padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#fca5a5", fontSize: 13, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" },
  bannerClose: { background: "none", border: "none", color: "inherit", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: "0 0 0 12px" },

  card: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, marginBottom: 14, overflow: "hidden" },
  cardHeader: { padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", justifyContent: "space-between", alignItems: "baseline" },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#e8eaf0" },
  cardSubtitle: { fontSize: 12, color: "#8892a4" },

  miniHead: { fontSize: 12, fontWeight: 700, color: "#818cf8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 },
  muted: { fontSize: 13, color: "#8892a4" },
  kvRow: { display: "flex", gap: 8, fontSize: 13, color: "#e8eaf0", padding: "5px 0", borderTop: "1px solid rgba(255,255,255,0.05)" },
  savedRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: "1px solid rgba(255,255,255,0.05)" },

  // Report doc
  reportDoc: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 28, marginBottom: 14 },
  reportHeader: { borderBottom: "2px solid rgba(129,140,248,0.4)", paddingBottom: 16, marginBottom: 8 },
  projectBlock: { padding: "18px 0", borderBottom: "1px solid rgba(255,255,255,0.07)" },
  projectTitle: { fontSize: 16, fontWeight: 700, color: "#e8eaf0", marginBottom: 4 },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: "#818cf8", textTransform: "uppercase", letterSpacing: "0.05em" },
  evRow: { display: "flex", gap: 10, padding: "6px 0", borderTop: "1px solid rgba(255,255,255,0.04)" },
  disclaimer: { marginTop: 18, fontSize: 11, color: "#8892a4", lineHeight: 1.6, fontStyle: "italic" },

  // Buttons / inputs (mirrors TimeAudit.jsx)
  btn: { padding: "8px 14px", background: "#1e2328", color: "#e8eaf0", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  btnPrimary: { padding: "8px 14px", background: "#a5ce3a", color: "#0d0f12", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  btnSm: { padding: "5px 10px", background: "#1e2328", color: "#e8eaf0", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  dateInput: { padding: "7px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit", colorScheme: "dark" },
  controlLabel: { fontSize: 12, color: "#8892a4", fontWeight: 600 },
  select: { padding: "6px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit" },
  txtInput: { padding: "7px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
  miniArea: { padding: "7px 10px", background: "#0d1117", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit", width: "100%", minHeight: 48, resize: "vertical", boxSizing: "border-box" },

  // Ledger
  ledgerRow: { display: "flex", gap: 12, padding: "10px 0", borderTop: "1px solid rgba(255,255,255,0.05)", alignItems: "flex-start" },
  rndPill: { marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#a5b4fc", background: "rgba(129,140,248,0.15)", border: "1px solid rgba(129,140,248,0.4)", borderRadius: 4, padding: "1px 5px", textTransform: "uppercase", letterSpacing: "0.04em" },
  rdLine: { fontSize: 11, color: "#a5b4cf", marginTop: 2, lineHeight: 1.45 },
};
